import { Check, Grid2X2, Loader2, Lock, Plus, RefreshCw, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../helpers/axiosConfig';
import { fetchSites, fetchStagingStatus } from '../../actions/sites';
import {
  getProvisioningStatus,
  openWpAdmin,
  retryProvisioning,
} from '../../helpers/provisioningApi';
import { provisioningMsLeft, statusToUi, userFullName } from '../../components/admin/adminUtils';
import SiteCard, { AddSiteCard } from '../../components/admin/SiteCard';

// Step 2 polls the real provisioning stage (US-009) — no blind countdown.
const PROVISION_POLL_MS = 4000;

// The server's pending_provisions.status drives the stage list. Each stage
// maps to a row; everything before the current stage renders as done.
const PROVISION_STAGES = [
  { key: 'pending', label: 'Cloning WordPress with North Commerce pre-installed' },
  { key: 'configuring', label: 'Configuring secure access' },
  { key: 'creating_admin', label: 'Creating your admin account' },
];
const stageIndex = (stage) => {
  if (stage === 'ready') return PROVISION_STAGES.length;
  const index = PROVISION_STAGES.findIndex((item) => item.key === stage);
  return index === -1 ? 0 : index;
};

const defaultCreateForm = (user) => ({
  siteName: '',
  wpAdminEmail: user?.email || '',
  wpAdminPassword: '',
});

const getErrorMessage = (error) => {
  if (!error) return '';
  if (typeof error === 'string') return error;
  if (error.msg) return error.msg;
  if (error.error) return error.error;
  if (Array.isArray(error.errors) && error.errors[0]?.msg) return error.errors[0].msg;
  return 'Something went wrong. Try again.';
};

function Stepper({ step }) {
  return (
    <div className="create-stepper">
      {[1, 2].map((item) => (
        <div className="create-step-group" key={item}>
          <span className={`create-dot ${item < step ? 'done' : ''} ${item === step ? 'active' : ''}`}>
            {item < step ? <Check size={12} /> : item}
          </span>
          {item < 2 ? <span className={`create-bar ${item < step ? 'done' : ''}`} /> : null}
        </div>
      ))}
    </div>
  );
}

function CreateSiteModal({ open, onClose }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth.user);
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(() => defaultCreateForm(user));
  const [error, setError] = useState('');
  const [createdSite, setCreatedSite] = useState(null);
  // Real backend stage: null until the site exists, then
  // pending | configuring | creating_admin | ready | failed (US-008 endpoint).
  const [stage, setStage] = useState(null);
  const [failedReason, setFailedReason] = useState('');
  const [wpLoginBusy, setWpLoginBusy] = useState(false);
  const [retryBusy, setRetryBusy] = useState(false);
  // True once the user backgrounds the create flow, so an in-flight POST that
  // resolves afterward does not take over the UI.
  const backgroundedRef = useRef(false);

  const updateField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const resetAndClose = () => {
    backgroundedRef.current = true;
    setStep(1);
    setError('');
    setCreatedSite(null);
    setStage(null);
    setFailedReason('');
    setForm(defaultCreateForm(user));
    onClose();
  };

  // Poll the real provisioning stage while the modal is on step 2 with a
  // created site. Stops on ready/failed and when the modal closes/unmounts.
  useEffect(() => {
    if (!open || !createdSite?.id) return undefined;
    if (stage === 'ready' || stage === 'failed') return undefined;

    let cancelled = false;
    const poll = async () => {
      try {
        const status = await getProvisioningStatus(createdSite.id);
        if (cancelled) return;
        setStage(status.stage);
        setFailedReason(status.stage === 'failed' ? status.failed_reason || '' : '');
        if (status.stage === 'ready') {
          const token = localStorage.getItem('CloudForge-Token');
          if (token) dispatch(fetchSites(token));
        }
      } catch (pollError) {
        // Transient poll failures just wait for the next tick.
      }
    };

    poll();
    const interval = setInterval(poll, PROVISION_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, createdSite, stage]);

  const createSite = async (event) => {
    event.preventDefault();
    setError('');

    if (form.wpAdminPassword.length < 10) {
      setError('WordPress admin password must be at least 10 characters.');
      return;
    }

    const token = localStorage.getItem('CloudForge-Token');
    if (!token) {
      setError('Your session expired. Sign in again to create a site.');
      return;
    }

    backgroundedRef.current = false;
    setStep(2);
    try {
      const response = await axiosInstance.post(
        'sites',
        {
          site_title: form.siteName,
          geo_affinity: 'dfw',
          php_workers: 3,
          is_burstable: false,
          email: form.wpAdminEmail,
          password: form.wpAdminPassword,
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'x-auth-token': token,
          },
        },
      );
      const nextSite = response.data?.site || response.data;
      dispatch(fetchSites(token));
      dispatch(fetchStagingStatus(token));
      // If the user backgrounded the flow while we were creating, don't take
      // over the UI — the new site's card carries the provisioning timer.
      if (!backgroundedRef.current) {
        setCreatedSite(nextSite);
        setStage('pending'); // polling takes over from here
      }
    } catch (requestError) {
      if (!backgroundedRef.current) {
        setError(getErrorMessage(requestError.response?.data) || requestError.message);
        setStep(1);
      }
    }
  };

  const handleWpLogin = async () => {
    if (!createdSite?.id) return;
    setWpLoginBusy(true);
    setError('');
    try {
      await openWpAdmin(createdSite.id);
    } catch (loginError) {
      setError(getErrorMessage(loginError.response?.data) || 'Could not open WordPress. Try again.');
    } finally {
      setWpLoginBusy(false);
    }
  };

  const handleRetry = async () => {
    if (!createdSite?.id) return;
    setRetryBusy(true);
    setError('');
    try {
      await retryProvisioning(createdSite.id);
      setFailedReason('');
      setStage('pending'); // resume polling
    } catch (retryError) {
      setError(getErrorMessage(retryError.response?.data) || 'Retry failed. Try again.');
    } finally {
      setRetryBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div className="modal-backdrop" role="presentation">
      <section className="create-modal" role="dialog" aria-modal="true" aria-labelledby="create-site-title">
        <header className="modal-head">
          <div className="modal-head-copy">
            <div className="modal-kicker">
              <span>STEP {step} OF 2</span>
              {step === 1 ? 'Name and access' : 'Provisioning'}
            </div>
            <Stepper step={step} />
          </div>
          <button className="icon-button" type="button" onClick={resetAndClose} aria-label="Close create site">
            <X size={16} />
          </button>
        </header>

        {step === 1 ? (
          <form onSubmit={createSite}>
            <div className="modal-body">
              <h2 id="create-site-title">Spin up a new staging site</h2>
              <p>Two fields. We handle hosted WordPress, North Commerce, Stripe-ready setup, and a free staging URL in under 90 seconds.</p>
              {error ? <div className="error-banner">{error}</div> : null}
              <div className="field-stack">
                <div className="form-field">
                  <label htmlFor="site-name">Site name</label>
                  <input
                    id="site-name"
                    type="text"
                    value={form.siteName}
                    onChange={(event) => updateField('siteName', event.target.value)}
                    required
                  />
                  <span className="hint">Shown in your admin and in the storefront title bar.</span>
                </div>
                <div className="form-field">
                  <label htmlFor="wp-admin-email">WordPress admin email</label>
                  <input
                    id="wp-admin-email"
                    type="email"
                    value={form.wpAdminEmail}
                    onChange={(event) => updateField('wpAdminEmail', event.target.value)}
                    required
                  />
                  <span className="hint">Becomes your WordPress admin username on the new site. Pre-filled from your North account.</span>
                </div>
                <div className="form-field">
                  <label htmlFor="wp-admin-password">WordPress admin password</label>
                  <input
                    id="wp-admin-password"
                    type="password"
                    minLength={10}
                    value={form.wpAdminPassword}
                    onChange={(event) => updateField('wpAdminPassword', event.target.value)}
                    required
                  />
                  <span className="hint">Your WordPress admin password for this site. At least 10 characters; never stored in plaintext.</span>
                </div>
              </div>
            </div>
            <footer className="modal-foot">
              <span>FREE WHILE IN STAGING FOR 30 DAYS</span>
              <div>
                <button className="btn btn-ghost" type="button" onClick={resetAndClose}>Cancel</button>
                <button className="btn btn-primary" type="submit">Create staging site</button>
              </div>
            </footer>
          </form>
        ) : null}

        {step === 2 ? (
          <>
            <div className="modal-body">
              <h2>
                {stage === 'ready' ? <><em>{form.siteName}</em> is ready</> : null}
                {stage === 'failed' ? <>Setup hit a snag on <em>{form.siteName}</em></> : null}
                {stage !== 'ready' && stage !== 'failed' ? <>Building <em>{form.siteName}</em></> : null}
              </h2>
              <p>
                {stage === 'ready'
                  ? 'Your store is live on its staging URL and your admin account is ready.'
                  : null}
                {stage === 'failed'
                  ? 'Your site was created, but we could not finish setting up your admin account. Retry setup below — no need to recreate the site.'
                  : null}
                {stage !== 'ready' && stage !== 'failed'
                  ? 'This usually takes a couple of minutes. You can run it in the background and we will refresh your dashboard when it is ready.'
                  : null}
              </p>
              {error ? <div className="error-banner">{error}</div> : null}
              {stage === 'failed' && failedReason ? (
                <div className="error-banner">{failedReason}</div>
              ) : null}

              <div className="provision-list">
                <div className={`provision-row ${createdSite ? 'done' : 'active'}`}>
                  {createdSite ? <Check size={14} /> : <Loader2 size={14} className="spin" />}
                  <span>Reserving your staging URL</span>
                  <strong>{createdSite ? 'Done' : 'Active'}</strong>
                </div>
                {PROVISION_STAGES.map((item, index) => {
                  const current = createdSite ? stageIndex(stage) : -1;
                  const isDone = current > index || stage === 'ready';
                  const isActive = current === index && stage !== 'failed' && stage !== 'ready';
                  const isFailedHere = stage === 'failed' && current === index;
                  let statusLabel = 'Pending';
                  if (isDone) statusLabel = 'Done';
                  else if (isActive) statusLabel = 'Active';
                  else if (isFailedHere) statusLabel = 'Failed';
                  return (
                    <div
                      className={`provision-row ${isDone ? 'done' : ''} ${isActive ? 'active' : ''}`}
                      key={item.key}
                    >
                      {isDone ? <Check size={14} /> : null}
                      {isActive ? <Loader2 size={14} className="spin" /> : null}
                      {!isDone && !isActive ? <span /> : null}
                      <span>{item.label}</span>
                      <strong>{statusLabel}</strong>
                    </div>
                  );
                })}
              </div>

              {stage === 'ready' && createdSite ? (
                <div className="provision-ready">
                  {createdSite.domain ? (
                    <p>
                      Your site:{' '}
                      <a href={`https://${createdSite.domain}`} target="_blank" rel="noreferrer">
                        {createdSite.domain}
                      </a>
                    </p>
                  ) : null}
                  <p>
                    Your WordPress admin username is <strong>{form.wpAdminEmail}</strong> with the
                    password you chose — or skip the password entirely:
                  </p>
                </div>
              ) : null}
            </div>
            <footer className="modal-foot">
              <span>BILLING NOT STARTED</span>
              <div>
                {stage !== 'ready' && stage !== 'failed' ? (
                  <button className="btn btn-ghost" type="button" onClick={resetAndClose}>Run in background</button>
                ) : (
                  <button className="btn btn-ghost" type="button" onClick={resetAndClose}>Close</button>
                )}
                {stage === 'failed' ? (
                  <button className="btn btn-primary" type="button" onClick={handleRetry} disabled={retryBusy}>
                    {retryBusy ? <Loader2 size={15} className="spin" /> : <RefreshCw size={15} />}
                    {retryBusy ? 'Retrying...' : 'Retry setup'}
                  </button>
                ) : null}
                {stage === 'ready' ? (
                  <>
                    <button
                      className="btn btn-ghost"
                      type="button"
                      onClick={() => createdSite?.id && navigate(`/sites/${createdSite.id}`)}
                    >
                      View site details
                    </button>
                    <button className="btn btn-primary" type="button" onClick={handleWpLogin} disabled={wpLoginBusy}>
                      {wpLoginBusy ? <Loader2 size={15} className="spin" /> : <Lock size={15} />}
                      {wpLoginBusy ? 'Opening...' : 'Log in to WordPress'}
                    </button>
                  </>
                ) : null}
              </div>
            </footer>
          </>
        ) : null}
      </section>
    </div>
  );
}

export default function SitesPage() {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const { data: sites, loading, error, stagingStatus } = useSelector((state) => state.sites);
  const [filter, setFilter] = useState('all');
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('CloudForge-Token');
    if (token) dispatch(fetchStagingStatus(token));
  }, [dispatch]);

  // Default to allowing creation until the status loads, so the UI isn't
  // blocked on a slow request. The backend is the authoritative gate.
  const canCreateStaging = stagingStatus ? stagingStatus.canCreateStaging : true;
  const stagingRemaining = stagingStatus
    ? Math.max(0, stagingStatus.stagingLimit - stagingStatus.stagingCount)
    : null;
  const atLimitMessage = stagingStatus?.hasActiveSubscription
    ? `You've reached your limit of ${stagingStatus?.stagingLimit} sites.`
    : `Free accounts are limited to ${stagingStatus?.stagingLimit} staging sites. Take a site live or subscribe to add more.`;

  const openCreate = () => {
    if (!canCreateStaging) return;
    setModalOpen(true);
  };

  const siteList = useMemo(() => (Array.isArray(sites) ? sites : []), [sites]);

  // While any site is still in its provisioning window, poll so its card flips
  // to ready once the backend finishes (SSL etc.) without a manual refresh.
  const hasProvisioningSite = useMemo(
    () => siteList.some((site) => provisioningMsLeft(site) > 0),
    [siteList],
  );
  useEffect(() => {
    if (!hasProvisioningSite) return undefined;
    const interval = setInterval(() => {
      const token = localStorage.getItem('CloudForge-Token');
      if (token) dispatch(fetchSites(token));
    }, 30000);
    return () => clearInterval(interval);
  }, [hasProvisioningSite, dispatch]);

  const counts = useMemo(() => {
    const initial = { all: siteList.length, live: 0, staging: 0, provisioning: 0, paused: 0 };
    siteList.forEach((site) => {
      const status = statusToUi(site.status);
      if (initial[status] !== undefined) initial[status] += 1;
    });
    return initial;
  }, [siteList]);

  const filteredSites = siteList.filter((site) => {
    if (filter === 'all') return true;
    return statusToUi(site.status) === filter;
  });

  if (loading && !siteList.length) {
    return <div className="loading-block">Loading sites</div>;
  }

  return (
    <>
      <CreateSiteModal open={modalOpen} onClose={() => setModalOpen(false)} />

      <div className="banner">
        <span className="banner-icon">i</span>
        {stagingStatus ? (
          <span>
            <strong>
              {stagingStatus.stagingCount} of {stagingStatus.stagingLimit}{' '}
              {stagingStatus.hasActiveSubscription ? '' : 'free '}staging sites used.
            </strong>{' '}
            {canCreateStaging
              ? `You can create ${stagingRemaining} more. Billing only starts when your domain points to a site.`
              : atLimitMessage}
          </span>
        ) : (
          <span>
            You have <strong>{counts.staging + counts.provisioning} staging sites</strong> not yet billed. Publish anytime - billing only starts when your domain points to the site.
          </span>
        )}
        <a href="/tutorials">Learn more</a>
      </div>

      <div className="page-head">
        <div>
          <h1 className="page-title">Your sites</h1>
          <div className="page-subtitle">
            {counts.all} sites - {counts.live} live - {counts.staging} staging - {counts.provisioning} provisioning
          </div>
        </div>
        <div className="page-actions">
          <div className="filter-tabs" aria-label="Site filters">
            {[
              ['all', 'All', counts.all],
              ['live', 'Live', counts.live],
              ['staging', 'Staging', counts.staging],
              ['paused', 'Paused', counts.paused],
            ].map(([key, label, count]) => (
              <button
                className={`filter-tab ${filter === key ? 'active' : ''}`}
                key={key}
                type="button"
                onClick={() => setFilter(key)}
              >
                {label} <span>{count}</span>
              </button>
            ))}
          </div>
          <button
            className="btn btn-primary"
            type="button"
            onClick={openCreate}
            disabled={!canCreateStaging}
            title={!canCreateStaging ? atLimitMessage : undefined}
          >
            <Plus size={16} />
            Add site
          </button>
        </div>
      </div>

      {!canCreateStaging ? (
        <div className="banner warning">
          <span className="banner-icon">!</span>
          <span>{atLimitMessage}</span>
          <a href="/billing">Manage billing</a>
        </div>
      ) : null}

      {error ? <div className="error-banner admin-page-error">{getErrorMessage(error)}</div> : null}

      {!siteList.length && !error ? (
        <div className="empty-state">
          <div className="empty-icon"><Grid2X2 size={24} /></div>
          <h3>No sites yet. Spin up your first staging store.</h3>
          <p>Give it a name and your WordPress admin password. We will handle the rest: installed, configured, and ready in under 90 seconds.</p>
          <div className="empty-actions">
            <button className="btn btn-primary" type="button" onClick={() => setModalOpen(true)}>
              <Plus size={16} />
              Add your first site
            </button>
            <a className="btn btn-ghost" href="/tutorials">Watch the walkthrough</a>
          </div>
        </div>
      ) : null}

      {siteList.length ? (
        <div className="sites-grid">
          {filteredSites.map((site) => <SiteCard key={site.id} site={site} />)}
          <AddSiteCard onClick={openCreate} disabled={!canCreateStaging} disabledReason={atLimitMessage} />
        </div>
      ) : null}

      <div className="sr-only">Signed in as {userFullName(user)}</div>
    </>
  );
}
